import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Grid,
  Snackbar,
  FormControlLabel,
  Checkbox
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { Header } from '../components-molecules';
import { ThemeProvider } from '@material-ui/core';
import { ITheme, colours, theme } from '../constants/theme';
import axios from 'axios';
import { setError } from '../redux/actions/error';
import ButtonWithLoading from "../components-atoms/ButtonWithLoadingIndicator";
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: ITheme) => ({
  paper: {
    padding: " 24px",
    display: "flex",
    flexDirection: "column",
    border: "1px solid rgba(0, 0, 0, 0.12)",
    boxSizing: "border-box",
    borderRadius: "20px",
    margin: "0",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxWidth: "inherit",
  },
  signIntitle: {
    marginBottom: 12,
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(3),
    alignItems: "center",
  },
  submit: {
    backgroundColor: colours.primary,
    borderRadius: "8px",
    padding: "6px 16px",
  },
  textField: {
    fontStyle: "normal",
    fontWeight: "normal",
    fontSize: "16px",
    lineHeight: "19px",
    letterSpacing: "0.25px",
    color: "rgba(0, 0, 0, 0.87)",
  },
  cssOutlinedInput: {
    "&$cssFocused $notchedOutline": {
      border: `1px solid ${theme.palette.primary.main}`,
    },
  },
  cssFocused: {},
  notchedOutline: {
    border: "1px solid rgba(0, 0, 0, 0.12)",
  },
  passwordStrength: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: '4px',
  },
  passwordWeak: {
    backgroundColor: '#ffebee',
  },
  passwordMedium: {
    backgroundColor: '#fff8e1',
  },
  passwordStrong: {
    backgroundColor: '#e8f5e9',
  },
}));

const Alert = (props: any) => {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
};

interface RouteParams {
  token: string;
}

export const ResetPassword: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { token } = useParams<RouteParams>();
  const history = useHistory();
  const dispatch = useDispatch();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong' | null>(null);
  
  // Check password strength
  useEffect(() => {
    if (!password) {
      setPasswordStrength(null);
      return;
    }
    
    // Check password strength
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const isLongEnough = password.length >= 10;
    
    const strength = 
      (hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChars && isLongEnough) 
        ? 'strong' 
        : (hasUpperCase && hasLowerCase && (hasNumbers || hasSpecialChars) && password.length >= 8)
          ? 'medium'
          : 'weak';
          
    setPasswordStrength(strength);
  }, [password]);
  
  const getPasswordStrengthMessage = () => {
    switch (passwordStrength) {
      case 'weak':
        return 'Weak password. Please use at least 10 characters with uppercase, lowercase, numbers, and special characters.';
      case 'medium':
        return 'Medium strength password. For better security, add more complexity.';
      case 'strong':
        return 'Strong password!';
      default:
        return '';
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password) {
      dispatch(setError('Please enter a password'));
      return;
    }
    
    if (password !== confirmPassword) {
      dispatch(setError('Passwords do not match'));
      return;
    }
    
    if (passwordStrength === 'weak') {
      dispatch(setError('Please use a stronger password'));
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post('/portal/forgot-password-reset', { 
        Token: token,
        Password: password 
      });
      setSuccess(true);
      setOpenSnackbar(true);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        dispatch(setError(error.response.data.error));
      } else {
        dispatch(setError('An error occurred. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };
  
  const redirectToLogin = () => {
    history.push('/');
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <Header />
        <Container component="main" maxWidth="xs">
          <Container className={classes.paper}>
            <ThemeProvider theme={theme}>
              <Typography
                component="h6"
                variant="h6"
                className={classes.signIntitle}
              >
                Reset Password
              </Typography>
              <Typography component="p" variant="body2" color="textSecondary">
                Enter your new password below.
              </Typography>
              
              {success ? (
                <Grid container spacing={2} style={{ marginTop: '20px' }}>
                  <Grid item xs={12}>
                    <Typography variant="body1">
                      Your password has been reset successfully. You can now log in with your new password.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} container justifyContent="center">
                    <ButtonWithLoading
                      onClick={redirectToLogin}
                      loading={false}
                      title={t('resetPassword.backToSignIn')}
                    />
                  </Grid>
                </Grid>
              ) : (
                <form className={classes.form} onSubmit={handleSubmit}>
                  <TextField
                    variant="outlined"
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label={t('resetPassword.enterNewPassword')}
                    type={showPassword ? "text" : "password"}
                    id="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    InputProps={{
                      classes: {
                        root: classes.cssOutlinedInput,
                        focused: classes.cssFocused,
                        notchedOutline: classes.notchedOutline,
                      },
                    }}
                  />
                  
                  {passwordStrength && (
                    <div className={`${classes.passwordStrength} ${classes[`password${passwordStrength.charAt(0).toUpperCase() + passwordStrength.slice(1)}`]}`}>
                      <Typography variant="body2">
                        {getPasswordStrengthMessage()}
                      </Typography>
                    </div>
                  )}
                  
                  <TextField
                    variant="outlined"
                    margin="normal"
                    required
                    fullWidth
                    name="confirmPassword"
                    label={t('resetPassword.confirmNewPassword')}
                    type={showPassword ? "text" : "password"}
                    id="confirmPassword"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    InputProps={{
                      classes: {
                        root: classes.cssOutlinedInput,
                        focused: classes.cssFocused,
                        notchedOutline: classes.notchedOutline,
                      },
                    }}
                  />
                  
                  <Grid
                    item
                    sm
                    container
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-start"
                    style={{ marginTop: '10px' }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          value="showPassword"
                          color="primary"
                          checked={showPassword}
                          onChange={(e) => setShowPassword(e.target.checked)}
                        />
                      }
                      label=""
                      style={{ marginRight: 0 }}
                    />
                    <Typography variant="body2" component="p">
                      Show password
                    </Typography>
                  </Grid>
                  
                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ marginTop: '20px' }}
                  >
                    <Grid item sm={8}>
                      <Button
                        variant="text"
                        color="primary"
                        onClick={redirectToLogin}
                      >
                        <Typography variant="button" component="p">
                          Back to Login
                        </Typography>
                      </Button>
                    </Grid>
                    <Grid item container justifyContent="flex-end" sm={4}>
                      <ButtonWithLoading
                        onClick={handleSubmit}
                        loading={loading}
                        title={t('resetPassword.title')}
                      />
                    </Grid>
                  </Grid>
                </form>
              )}
            </ThemeProvider>
          </Container>
        </Container>
        
        <Snackbar 
          open={openSnackbar} 
          autoHideDuration={6000} 
          onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity="success">
            Password reset successfully!
          </Alert>
        </Snackbar>
      </ThemeProvider>
    </>
  );
};
